<template>
  <div id="TCs" class="container-fluid px-0">
    <div class="main">
      <div class="page-header">
        <div class="page-title d-flex flex-column align-items-center">
          <img class="logo" src="/img/spectrum_life_logo_light.png" />
          <h2 class="brand-text mb-0">
            Terms of Use
          </h2>
        </div>
      </div>
      <div class="content">
        <div class="content-card">
          <h3 class="pt-0">Terms of Use for Service</h3>
          <p>SPECTRUM WELLNESS UK LTD T/A Spectrum.Life</p>

          <h3>Definitions</h3>
          <p>
            <b>"Services"</b> &mdash; Refers to the Virtual Primary Care (VPC) service available to You through the
            Spectrum.Life platform, comprising GP and ANP consultations, as provided under the arrangement between
            Spectrum.Life and your employer, insurer, or organisation. The specific services available to You will
            depend on the plan or package arranged by your employer, insurer, or organisation.
          </p>
          <p>
            <b>"User" or "You"</b> &mdash; Refers to any individual accessing or using the Services, including
            members and beneficiaries accessing Services through an insurer or employer arrangement.
          </p>
          <p>
            <b>"Platform"</b> &mdash; Refers to the Spectrum.Life app or web portal (iOS and Android) through which
            the Services are accessed.
          </p>
          <p>
            <b>"Clinician"</b> &mdash; Refers to qualified professionals providing the Services, including General
            Practitioners (GPs) and Advanced Nurse Practitioners (ANPs).
          </p>
          <p>
            <b>"Spectrum.Life"</b> &mdash; SPECTRUM WELLNESS UK LTD T/A Spectrum.Life, a company incorporated in
            England and Wales with registered number 11815511, registered address: Habib House, 9 Stevenson Square,
            Manchester M1 1DB, UK.
          </p>
          <p>
            <b>"Consultation"</b> &mdash; A clinical appointment of up to 15 minutes delivered remotely by video or
            voice call, whether through the Platform or by telephone, between You and a Clinician.
          </p>
          <p>
            <b>"Content"</b> &mdash; Any text, graphics, images, audio, video, software, data compilations, page
            layout, underlying code, software, or other information appearing on or forming part of the Service.
          </p>

          <h3>Introduction</h3>
          <p>
            These Terms of Use apply between You, the User of the Spectrum.Life Virtual Primary Care service (&lsquo;the
            Service&rsquo;), and Spectrum Wellness UK Limited T/A Spectrum.Life (&lsquo;Spectrum.Life&rsquo;), the provider of
            the Service.
          </p>
          <p>
            You are accessing these Services as a member or beneficiary of an arrangement between Spectrum.Life and
            your employer, insurer, or organisation. These Terms govern Your personal use of the Service and set out
            Your rights and responsibilities as a User.
          </p>
          <p>
            VPC is designed to complement, not replace, in-person NHS or private care. Clinicians cannot access your
            NHS records directly, though you are welcome to share relevant information, letters, or test results
            during your Consultation. Using this Service does not affect your NHS registration or entitlement in any
            way.
          </p>
          <p>
            By using the Service, You agree to be bound by these Terms. If You do not agree, please stop using the
            Service.
          </p>

          <h3>Intellectual Property and Acceptable Use</h3>
          <p>
            All Content included in the Service is the property of Spectrum.Life, our affiliates, or other relevant
            third parties. By continuing to use the Service You acknowledge that such Content is protected by
            copyright, trademarks, database rights, and other intellectual property rights.
          </p>
          <p>
            You may, for Your own personal, non-commercial use only: retrieve, display, and view Content via the
            Service. You must not otherwise reproduce, modify, copy, distribute, or use for commercial purposes any
            Content without the written permission of Spectrum.Life.
          </p>

          <h3>Fair Use</h3>
          <p>
            VPC is provided for the personal healthcare needs of Authorised Users and is not intended to replace
            ongoing specialist or long-term clinical management better suited to NHS or in-person care. Spectrum.Life
            monitors usage to identify patterns that may indicate the Service is being used outside its intended
            scope &mdash; for example, an unusually high frequency of consultations by a single Authorised User within a
            short period, use of the Service by someone other than the registered Authorised User, or repeated
            requests for services listed under &ldquo;What is Not Included.&rdquo;
          </p>
          <p>
            Where such a pattern is identified, a Clinician or the Clinical Governance Board may review the case and,
            where clinically appropriate, discuss the pattern of use with the Authorised User and recommend an
            alternative care pathway. Spectrum.Life may discuss patterns of service use with your employer, insurer
            or organisation where necessary to support appropriate management of access. Any such discussion will be
            on an anonymised basis unless the Authorised User has provided explicit consent for identifiable
            information to be shared, or where disclosure is otherwise required or permitted by law. Spectrum.Life
            will not withdraw or restrict access to the Service on this basis without first attempting to understand
            the clinical reason for the pattern of use.
          </p>
          <p>
            This clause does not limit or replace the clinical judgement of any individual Clinician in a
            Consultation and does not create a fixed numerical cap on consultations.
          </p>

          <h3>Health Disclaimer</h3>
          <p>
            The Services provided by Spectrum.Life are not a substitute for emergency medical care. If you have an
            emergency, call 999 or attend A&amp;E immediately.
          </p>
          <p>
            If you believe that you are in an emergency or life-threatening situation you should contact your local
            Emergency Services immediately, especially if you, or someone: has severe difficulty breathing or is not
            breathing; is having a heart attack or stroke; has severe chest or abdominal pain; has severe bleeding;
            has lost consciousness; is in an acute confused state and/or having fits which are not stopping; is in
            suicidal distress; or is experiencing an acute mental health crisis.
          </p>

          <h3>Services, Consultation Scope and Exclusions</h3>
          <p>
            Services are currently offered to members located within the United Kingdom, the Channel Islands, and
            the Isle of Man. You can access the Service from most countries when travelling, with the exception of
            the United States and Canada. Please note that prescriptions and referrals are only valid within the UK,
            Channel Islands, and Isle of Man; if you are outside these territories at the time of your Consultation,
            these cannot be issued.
          </p>
          <p>
            Services are delivered in accordance with regulatory and clinical standards including: GMC, NMC, CQC,
            and NICE guidelines, overseen by Spectrum.Life&rsquo;s internal Clinical Governance Board.
          </p>
          <p>
            A safeguarding board is maintained to ensure the safety and wellbeing of Users, and Clinicians are
            trained in safeguarding protocols.
          </p>
          <p>
            Spectrum.Life reserves the right to decline access to Services if it is determined that You do not meet
            the Service&rsquo;s clinical or eligibility criteria. In such cases, Spectrum.Life will make every effort to
            guide You towards appropriate alternative services or resources.
          </p>

          <h3>What the Service Can Help With</h3>
          <p>
            The VPC can support most non-emergency medical concerns, including: common illnesses; ongoing health
            conditions such as asthma and hypertension, where clinically appropriate; mental health concerns such as
            stress, anxiety, and low mood; women's and men's health issues; medication reviews and repeat
            prescriptions for stable long-term conditions, where clinically appropriate; and referrals to private
            specialists, where clinically indicated.
          </p>

          <h3>GP, ANP Consultations</h3>
          <p>
            Consultations are delivered remotely by UK-qualified General Practitioners (GPs) and Advanced Nurse
            Practitioners (ANPs) via video or voice call &mdash; you choose at the point of booking. Each Consultation is
            up to 15 minutes and conducted in English.
          </p>
          <p>
            Please ensure you are in a private location and able to give the Clinician your full attention during
            your Consultation. You must not be driving or operating machinery. The Clinician may end the Consultation
            if they consider it clinically unsafe to continue.
          </p>
          <p>
            Consultations are not recorded. A range of clinical audits are carried out on both digital and telephone
            consultations as part of Spectrum.Life&rsquo;s ongoing clinical governance and quality assurance.
          </p>

          <h3>Mental Health Support</h3>
          <p>
            GPs can provide mental health support, medication advice, and onward referrals to therapy or psychiatry
            where clinically appropriate. If there is a safety concern, your Clinician will signpost you to NHS or
            emergency services to ensure you receive the right level of care as quickly as possible.
          </p>

          <h3>Pregnancy</h3>
          <p>
            Pregnancy is not a bar to using the Service. Where you indicate pregnancy during the booking process, you
            will be asked to complete additional clinical screening questions to ensure you are directed to the most
            appropriate care pathway. Some presentations may be directed to a more appropriate service.
          </p>

          <h3>Clinical Independence</h3>
          <p>
            All clinical decisions &mdash; including which Clinician you see, whether a prescription is issued, and
            whether you are referred elsewhere &mdash; are made solely by Spectrum.Life&rsquo;s clinical team. Your
            organisation, your employer, and you cannot direct or override clinical decisions. This protects the
            independence and integrity of the Service.
          </p>

          <h3>What is Not Included</h3>
          <p>
            Emergency or urgent care; NHS-funded services or services that duplicate NHS primary care entitlements;
            inpatient, secondary, or specialist care beyond that described above; physical examinations, imaging, or
            blood tests arranged directly; ongoing statutory fit notes for long-term sick leave (private fit notes
            available for absences up to 14 days); direct NHS referrals, investigations, or tests; pregnancy or
            antenatal scanning, immunisations, or travel vaccinations; physiotherapy (optional add-on, not included
            as standard); and services for users physically located in the United States or Canada at the time of
            access.
          </p>

          <h3>Prescriptions</h3>
          <p>
            Where a Clinician determines that prescription medication is clinically appropriate, they may issue a
            Private Prescription at their sole clinical discretion. A Private Prescription is not guaranteed.
          </p>
          <p>
            Prescriptions, including repeat prescriptions, are only issued following a clinical Consultation with a
            Clinician. There is no separate process by which a prescription can be requested or issued without a
            Consultation.
          </p>
          <p>
            Repeat prescriptions are available for stable long-term conditions where it is clinically appropriate and
            safe to do so. Your GP will review your current medication and may provide a repeat prescription, subject
            to clinical judgement and safe prescribing standards.
          </p>
          <p>
            Spectrum.Life will prescribe the most affordable clinically appropriate option. Prescriptions are issued
            electronically. Following your Consultation, you will receive an email containing your prescription code
            and a text message containing a code redeemable at any pharmacy in the UK, Channel Islands, or Isle of
            Man.
          </p>
          <p>
            Home delivery is also available. Your prescription will include a link to request delivery, which our
            pharmacy delivery partner will arrange directly with you.
          </p>
          <p>
            You will be asked to confirm your location at the point of booking. Prescriptions are only valid within
            the UK, Channel Islands, and Isle of Man &mdash; they cannot be issued if you are outside these
            jurisdictions at the time of your Consultation.
          </p>

          <h3>Medications That Cannot Be Prescribed</h3>
          <p>
            Controlled drugs and opiates including codeine and dihydrocodeine; other controlled drugs or drugs of
            potential abuse including pregabalin and gabapentin; medications requiring specialist input or ongoing
            monitoring (e.g. DMARDs, Warfarin, ADHD medications, weight-loss medications); and gender-affirming
            hormone therapy, which must not be initiated, monitored, or adjusted within the Service under any
            circumstances.
          </p>
          <p>
            Antibiotics may only be prescribed where there is clear clinical evidence of bacterial infection, in line
            with antimicrobial stewardship principles and NICE/UKHSA guidance. In all cases where a restricted
            medication is requested, the Clinician will explain the policy and direct You to the appropriate service.
          </p>

          <h3>Fit Notes</h3>
          <p>
            GPs can issue private fit notes for absences of up to 14 days where clinically appropriate following a
            Consultation. Ongoing statutory fit notes for long-term sick leave are not available through this
            Service.
          </p>

          <h3>Identity Verification</h3>
          <p>
            To protect your safety and the integrity of the Service, you are required to verify your identity before
            using the Service. As part of your initial registration, you will be asked to upload a form of formal
            identification to the Platform before you can access the Service.
          </p>
          <p>
            Accepted forms of photo ID are: Passport; UK or EU driving licence; Biometric residence permit; EEA
            national identity card.
          </p>
          <p>
            At every Consultation, the Clinician will also carry out a three-point check, confirming your full name,
            date of birth, and address. If there is any doubt about identity, the Clinician will not proceed and will
            escalate to the on-call Clinical Lead.
          </p>
          <p>
            If you access the Service by telephone, you can submit your identification by secure email instead of
            uploading it to the Platform. Your Consultation will not go ahead until your identification has been
            received and verified.
          </p>

          <h3>Children and Young People</h3>
          <p>
            Children aged 0&ndash;15: a parent or legal guardian must consent and be present during the Consultation.
            The parent or guardian books the appointment via their own account. For children, a passport or, where a
            passport is not available, a birth certificate confirming the child&rsquo;s identity, must be uploaded before
            the appointment. A Gillick competency assessment will be carried out for under-16s.
          </p>
          <p>
            Age 16&ndash;21: parental consent is not required. The individual must have their own account.
          </p>

          <h3>User Responsibilities</h3>
          <p>
            You must provide Spectrum.Life with accurate and complete personal and medical information to ensure
            appropriate and effective service delivery. You are expected to follow the recommendations provided by
            the assigned Clinician.
          </p>
          <p>
            You are responsible for managing your appointment schedule. If you booked your appointment through the
            Platform, it can only be cancelled or rescheduled through the Platform. To cancel: log in via the web or
            app; open your appointments list and go to the Upcoming tab; on the appointment card, click Cancel;
            confirm your cancellation. A cancellation confirmation email will be sent to you immediately, including
            rebooking instructions.
          </p>
          <p>
            Where you are able, please give at least one (1) hour's notice if you need to cancel an appointment. This
            helps ensure the appointment can be offered to another Authorised User.
          </p>
          <p>
            If you booked your appointment by telephone, it must be cancelled by telephone by calling the same
            number. It cannot be cancelled through the Platform.
          </p>

          <h3>Appointment Notifications and Reminders</h3>
          <p>
            When you book an appointment, you will receive a booking confirmation email and an in-app notification.
            You will also receive reminders: at the time of booking, 24 hours before, and (app only) push
            notifications at 24 hours, 1 hour, and 5 minutes before your appointment.
          </p>
          <p>
            Parent(s) or guardian(s) are responsible for supervising children and young people engaging with the
            Service and must remain within the same environment during the session.
          </p>
          <p>
            Spectrum.Life operates a zero tolerance approach towards abusive, aggressive, or threatening behaviour
            directed at any member of its clinical or support staff, and reserves the right to terminate a
            Consultation or withdraw access to the Service if such behaviour is exhibited.
          </p>

          <h3>Telephone Services</h3>
          <p>
            These Terms apply to both digital and telephone access to the Service. Appointments can be booked 24
            hours a day, seven days a week. To book by telephone, please call +44 20 81385884. If you booked by
            telephone, you can also cancel or amend your appointment by calling the same number. GP consultations are
            available 24 hours a day, seven days a week. ANP consultations are available between 06:00 and 01:00,
            though you can book an ANP appointment at any time. If you call between 06:00 and 01:00, you will be
            connected to an operator who will book your appointment. If you call between 01:00 and 06:00, you will be
            asked to leave your name and phone number, and a member of our team will call you back during operating
            hours to complete your booking. Your Clinician will call you directly at the time of your appointment
            &mdash; no app or online platform is needed to use the telephone service.
          </p>

          <h3>Third-Party Links and Services</h3>
          <p>
            The Platform may integrate with third-party services, such as payment gateways and other service
            providers. Spectrum.Life is not responsible for issues arising from these external systems.
          </p>

          <h3>Privacy and Data Protection</h3>
          <p>
            All personal and clinical data is processed in accordance with the UK General Data Protection Regulation
            (UK GDPR) and the Data Protection Act 2018. All data is encrypted in transit and at rest. Access to
            clinical data is role-based and logged. Our systems follow ISO 27001 standards.
          </p>
          <p>
            Personal data processed in connection with the Service includes your name and contact details,
            appointment details and consultation logs, special category health data including presenting issues and
            clinical outcomes, and technical metadata.
          </p>
          <p>
            Data is retained as follows: general personal data &mdash; during active use, then up to 6 years after
            account deactivation; GP medical records &mdash; minimum 10 years from date of last consultation; mental
            health clinical records &mdash; minimum 7 years from date of last consultation; data relating to minors
            &mdash; until the individual reaches 25 years of age, or longer if required by law.
          </p>
          <p>
            Information about how your data may be used for outcome reporting purposes is set out in our Privacy
            Policy.
          </p>
          <p>
            For privacy-related concerns or to exercise your data rights, please contact Spectrum.Life&rsquo;s Data
            Protection Officer at:
            <a href="mailto:gdprspectrumlife@spectrum.life">gdprspectrumlife@spectrum.life</a>. You can also lodge a
            complaint with the UK Information Commissioner&rsquo;s Office at
            <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer">ico.org.uk</a>.
          </p>
          <p>
            For children aged 0&ndash;15, data is processed under Article 6(1)(a) (consent of the parent or legal
            guardian) and Article 9(2)(h) (provision of health or social care services).
          </p>

          <h3>Confidentiality</h3>
          <p>
            As a general rule, your Clinician will keep the information you share with them in your Consultations
            private. Exceptions apply where there is risk of serious harm to you or others, where a person under 18
            is being or has been abused, where a legal order overrides confidentiality, or where required by law or
            safeguarding obligations. Where any of these situations arise, this will be discussed openly with you
            wherever possible.
          </p>

          <h3>Availability of the Service and Disclaimers</h3>
          <p>
            Any online facilities, tools, services, or information that Spectrum.Life makes available through the
            Service are provided &lsquo;as is&rsquo; and on an &lsquo;as available&rsquo; basis. Spectrum.Life accepts no liability
            for any disruption or non-availability of the Service.
          </p>

          <h3>Limitation of Liability</h3>
          <p>
            Nothing in these Terms of Use will limit or exclude liability for death or personal injury resulting from
            negligence, for fraud or fraudulent misrepresentation, or in any way not permitted under applicable law.
            We will not be liable for losses arising out of events beyond our reasonable control.
          </p>

          <h3>Complaints and Feedback</h3>
          <p>
            You can raise a complaint about the VPC service or a Consultation in either of the following ways:
          </p>
          <ul>
            <li>
              Contact Simplyhealth customer service, who will send you a consent form and pass your complaint to
              Spectrum.Life on your behalf; or
            </li>
            <li>Contact Spectrum.Life directly by telephone on +44 20 81385884</li>
          </ul>
          <p>
            All complaints will be acknowledged within one (1) Business Day and resolved within ten (10) Business
            Days from the date consent is received, unless further investigation is required.
          </p>

          <h3>General</h3>
          <p>
            You may not transfer any of your rights under these Terms of Use to any other person. These Terms of
            Use, together with the Privacy Policy, govern Your use of the Service. The Contracts (Rights of Third
            Parties) Act 1999 shall not apply to these Terms of Use.
          </p>
          <p>
            This agreement shall be governed by and interpreted according to the law of England and Wales. All
            disputes shall be subject to the exclusive jurisdiction of the English and Welsh courts.
          </p>

          <h3>Spectrum Wellness UK Ltd T/A Spectrum.Life &mdash; Company and Contact Details</h3>
          <p>
            SPECTRUM WELLNESS UK LTD T/A Spectrum.Life is a company incorporated in England and Wales with registered
            number 11815511, whose registered address is Habib House, 9 Stevenson Square, Manchester M1 1DB, UK.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "TermsAndConditionsVpc"
};
</script>

<style lang="less">
#TCs {
  .main {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
  }

  .page-header {
    padding: 60px 20px 20px;
    background-color: #fbfafa;
  }

  .logo {
    max-width: 350px;
    width: 100%;
    padding-bottom: 50px;
  }

  .page-title {
    h2 {
      text-align: center;
      font-size: 3.8rem;
      letter-spacing: normal;
      font-family: "PlusJakartaSansBold";
      margin-bottom: 0;
    }
  }

  .subheading {
    font-size: 2.8rem;
    text-align: left;
    margin-bottom: 20px;
  }

  .table {
    gap: 6px;
  }

  .content {
    flex: 1 1 100%;
    padding: 20px 20px 60px;
    background-color: #fbfafa;
  }

  .content-card {
    height: 100%;
    background: #ffffff;
    max-width: 1000px;
    padding: 40px 60px 30px;
    margin: 0 auto;
    border-radius: 16px;

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      color: #212529;
      font-family: "PlusJakartaSansBold";
    }

    h3 {
      font-size: 2.2rem;
      letter-spacing: 0;
      padding: 32px 0 0;
      margin: 0 0 24px;
    }

    h4 {
      font-size: 1.6rem;
      letter-spacing: 0;
      padding: 0;
      margin: 0 0 4px;

      &.underline {
        text-decoration: underline;
      }
    }

    p {
      color: #212529;
      padding: 0;
      margin: 0 0 16px;
      font-size: 1.6rem;
    }

    ul,
    ol {
      color: #212529;
      margin: 0 0 16px;
    }

    li {
      color: #212529;
      font-size: 1.6rem;
    }

    b {
      font-family: "PlusJakartaSansBold";
    }

    a {
      color: #3c16f9;

      &:hover,
      &:active {
        color: #3c16f9;
      }
    }
  }

  .top-list {
    li {
      margin-bottom: 6px;
    }

    a {
      color: #3c16f9;

      &:hover,
      &:active {
        color: #3c16f9;
      }
    }
  }

  @media only screen and (max-width: 480px) {
    .content-card {
      padding: 30px 20px;
    }
  }
}
</style>
